/*  MUI  */
import { ExpandMore } from '@mui/icons-material';
import { MenuItem, Select } from '@mui/material';
import { Route, SessionHelper } from 'application/Shared/Helpers';
import { useSession } from 'hooks/redux/Selectors/session';
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';

/*  React  */
import React from 'react';
import { useNavigate, useParams } from 'react-router';

// When a global admin is viewing a tenant that's not their own, we want to dynamically add that tenant's link
// into the selector, so that the user knows which tenant they are viewing.
export const ensureCurrentlyViewedTenantIsFoundInLinks = (
    flattenedLinks,
    currentlyViewedPartnerTenant,
    currentlyViewedClientTenant
) => {
    if (!currentlyViewedClientTenant && !currentlyViewedPartnerTenant) {
        return flattenedLinks;
    }

    // Ensure that the currently viewed client or partner tenant is included in the links.
    const foundCurrentlyViewedTenant = flattenedLinks.find(
        (l) =>
            (!!currentlyViewedClientTenant &&
                l.clientId === currentlyViewedClientTenant?.id) ||
            (!!currentlyViewedPartnerTenant &&
                l.partnerId === currentlyViewedPartnerTenant?.id)
    );

    // If not, then we need to add the currently viewed tenant's info to the list of links so that
    // the name can be displayed and the selector isn't empty.
    // (a console warning is shown when the selected tenant is not an option in the Select component)
    if (!foundCurrentlyViewedTenant) {
        const tenantInfoLinkToAdd =
            currentlyViewedClientTenant || currentlyViewedPartnerTenant;
        return [
            {
                clientId: currentlyViewedClientTenant?.id,
                name: tenantInfoLinkToAdd.name,
                partnerId: currentlyViewedPartnerTenant?.id
            },
            ...flattenedLinks
        ];
    }

    return flattenedLinks;
};

const flattenTenantLinks = (
    tenantLinks,
    sessionTenantId,
    isSystemAdmin,
    canViewInternalDeployments
) =>
    tenantLinks.reduce((acc, link) => {
        // Only 'Default Admins' and 'Client Admins' have access to view linked partner tenants that aren't their own.
        if (
            isSystemAdmin ||
            (!isSystemAdmin && link.partnerId === sessionTenantId)
        ) {
            acc.push(link);
            const { targetTenantClients, partnerInternalId } = link;
            targetTenantClients.forEach((client) => {
                // Check if the current user has permission to view internal deployments.
                // If not, don't show them in the list.
                if (
                    !canViewInternalDeployments &&
                    // If a client's ID matches the partner's internal ID, it's an internal deployment.
                    partnerInternalId === client.id
                ) {
                    return;
                }
                acc.push({
                    clientId: client.id,
                    name: client.name,
                    partnerId: link.partnerId
                });
            });
        }
        return acc;
    }, []);

const renderMenuItems = ({
    currentlyViewedClientTenant,
    currentlyViewedPartnerTenant,
    navigate,
    session,
    tenantLinks
}) => {
    const sessionHelper = new SessionHelper(session);
    const sessionTenantId = sessionHelper.getTenantId();
    const canViewInternalDeployments = sessionHelper.hasInternalAccess();
    const isSystemAdmin = sessionHelper.hasPermission('system_admin');

    // Select component requires MenuItem children be flat and not wrapped by Fragments,
    // so this flattens the links as needed.
    const flattenedLinks = flattenTenantLinks(
        tenantLinks,
        sessionTenantId,
        isSystemAdmin,
        canViewInternalDeployments
    );

    const updatedLinks = ensureCurrentlyViewedTenantIsFoundInLinks(
        flattenedLinks,
        currentlyViewedPartnerTenant,
        currentlyViewedClientTenant
    );

    return updatedLinks.map((link) => {
        const { partnerId, clientId } = link;
        const baseRoute = clientId
            ? Route.buildBaseAdminRoute({ clientId, partnerId })
            : Route.buildBasePartnerRoute({ partnerId });

        const route = `${baseRoute}${clientId ? '' : '/dashboard'}`;
        const id = clientId || partnerId;
        return (
            <MenuItem
                data-testid={`partnerTenantSelectorMenuItem_${id}`}
                key={id}
                value={id}
                onClick={() => navigate(route)}
                style={{ paddingLeft: clientId ? '32px' : '16px' }}
            >
                {link.name}
            </MenuItem>
        );
    });
};

export const TenantSelector = () => {
    const session = useSession();
    const navigate = useNavigate();

    const { partnerId, clientId } = useParams();
    // We need the currently viewed partner and client info to be able to potentially add them to the tenant selector
    // in cases where a global admin is viewing a tenant that isn't their own.
    const partnerTenantInfo = useTenantInfoByTenantId(partnerId);
    const partnerTenant = partnerTenantInfo?.tenant;
    const clientTenantInfo = useTenantInfoByTenantId(clientId);
    const clientTenant = clientTenantInfo?.tenant;

    const sessionHelper = new SessionHelper(session);
    const partnerTenantLinks = sessionHelper.getPartnerTenantLinks();

    if (!partnerTenantLinks?.length) {
        return null;
    }

    const tenantLinks = partnerTenantLinks.map((link) => ({
        clientId: null,
        name: link.targetTenant.name,
        partnerId: link.targetTenant.id,
        partnerInternalId: link.targetTenant.internalId,
        targetTenantClients: link.targetTenantClients
    }));

    const initialValue = clientId || partnerId || sessionHelper.getTenantId();

    return (
        <Select
            className="asmTenantSelector"
            data-testid="partnerTenantSelector"
            IconComponent={ExpandMore}
            sx={{
                backgroundColor: 'tenantSelector.input.backgroundColor',
                borderColor: 'tenantSelector.input.borderColor'
            }}
            MenuProps={{
                MenuListProps: {
                    sx: {
                        color: 'tenantSelector.menuListColor'
                    }
                },
                PaperProps: {
                    elevation: 24,
                    sx: {
                        '& .MuiMenuItem-root': {
                            color: 'tenantSelector.paperProps.item.color'
                        },

                        '& .MuiMenuItem-root.Mui-selected:hover': {
                            backgroundColor:
                                'tenantSelector.paperProps.itemSelectedHover.backgroundColor',
                            color: 'tenantSelector.paperProps.itemSelectedHover.color'
                        },
                        '& .MuiMenuItem-root:hover': {
                            backgroundColor:
                                'tenantSelector.paperProps.item.hover.backgroundColor',
                            color: 'tenantSelector.paperProps.item.hover.color'
                        },
                        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                        '& .MuiMenuItem-root.Mui-selected': {
                            '&:hover': {
                                backgroundColor:
                                    'tenantSelector.paperProps.itemSelected.hover.backgroundColor'
                            },
                            backgroundColor:
                                'tenantSelector.paperProps.itemSelected.backgroundColor',
                            color: 'tenantSelector.paperProps.itemSelected.color',
                            fontWeight: 600
                        },
                        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                        '& .MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
                            '&:hover': {
                                backgroundColor:
                                    'tenantSelector.paperProps.itemSelected.hover.backgroundColor'
                            },
                            backgroundColor:
                                'tenantSelector.paperProps.itemSelected.backgroundColor',
                            color: 'tenantSelector.paperProps.itemSelected.color',
                            fontWeight: 600
                        },
                        backgroundColor:
                            'tenantSelector.paperProps.backgroundColor',
                        border: '1px solid',
                        borderColor: 'tenantSelector.paperProps.borderColor',
                        left: '10px !important',
                        maxHeight: '400px',
                        paddingX: 0,
                        width: '375px'
                    }
                }
            }}
            value={initialValue}
            variant="standard"
        >
            {/*
                Tried making this a component instead of a function, but the Select component had issues
                matching the initial value to one of the children. Using a function seems to resolve the issue.
            */}
            {renderMenuItems({
                currentlyViewedClientTenant: clientTenant,
                currentlyViewedPartnerTenant: partnerTenant,
                navigate,
                session,
                tenantLinks
            })}
        </Select>
    );
};
